<script context="module">
  export const PICKER_WIDTH_DEFAULT = 293;
  export const PICKER_HEIGHT_DEFAULT = 335;
  export const PICKER_WIDTH_EXPANDED = 547;
  export const PICKER_HEIGHT_EXPANDED = 532;
  export const EXPANSION_TIME_MS = 300;
</script>

<script lang="ts">
  import EmojiExpansion from "./EmojiExpansion.svelte";
  import VirtualList from "svelte-virtual-list-ce";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import type { Emoji } from "./types";
  import { fade } from "svelte/transition";
  import { localStorageStore } from "$stores/localStorage";
  import { onMount, tick } from "svelte";
  import { createEventDispatcher } from "svelte";
  import { EMOJI_SIZE } from "./EmojiPicker.svelte";
  import SkinTonePicker from "./SkinTonePicker.svelte";
  import cx from "classnames";
  import type { Placement } from "tippy.js";
  import { Cross, Search } from "$components/+blocks/OVIcon";

  export let placement: Placement = "top-start";

  let pickerWidth = PICKER_WIDTH_DEFAULT;
  let pickerHeight = PICKER_HEIGHT_DEFAULT;
  let expanded = false;
  // the expansion button is only visible if the picker is not too close to the edge of the screen
  let showExpansion = false;
  $: rowLength = Math.floor(pickerWidth / EMOJI_SIZE);

  let dispatch = createEventDispatcher();
  let recentEmojis = localStorageStore<Emoji[]>("recent-emojis", []);
  let allEmojisRows: Emoji[][];
  let allEmojisRowsIndexed: [Emoji[], number][];

  let skinIndex = localStorageStore<number>("emoji-skin-index", 0);
  let showSkinTones = false;

  let filter: string | undefined = undefined;
  let inputRef: HTMLInputElement;
  let emojiPickerRef: HTMLDivElement;

  let virtualListContainerRef: HTMLDivElement;
  let viewableStartIndex: number;
  let viewableEndIndex: number;
  let emojisLabels: Emoji[] = [];
  onMount(async () => {
    // wait for the the dom to complete the transition before checking if the picker is too close to the edge of the screen
    await tick();
    showExpansion =
      (placement === "bottom-start" &&
        window.innerHeight - emojiPickerRef?.getBoundingClientRect().bottom > 250) ||
      (placement === "top-start" && emojiPickerRef?.getBoundingClientRect().top > 250);
    // focus on the input
    inputRef?.focus();
    const emoji = await import("./emojisLabels");
    emojisLabels = emoji.emojisLabels;
  });

  // this is the list of filtered emojis, if there is no filter applied, it will be the same as the emojisLabels list
  $: filteredEmojis = !filter
    ? emojisLabels
    : emojisLabels.filter((emoji) =>
        emoji.keywords.some(
          (keyword) =>
            keyword.toLowerCase().includes(filter!.toLowerCase()) ||
            emoji.name.toLowerCase().includes(filter!.toLowerCase())
        )
      );

  // this generates a list of all emojis rows, the row's length is determined by the rowLength variable, including the recent emojis row as the first row if there is no filter applied
  $: {
    allEmojisRows = filteredEmojis.reduce((acc: any[], curr, i) => {
      if (i % rowLength === 0) {
        acc.push([]);
      }
      acc[acc.length - 1].push(curr);
      return acc;
    }, []);

    if (!filter) {
      allEmojisRows = [$recentEmojis, ...allEmojisRows];
    }

    allEmojisRowsIndexed = allEmojisRows.map((e, i) => [e, i]);
  }

  // the height for the virtual list is calculated by subtracting the bottom of the picker from the top of the virtual list
  $: scrollableHeight = Math.floor(
    pickerHeight -
      (virtualListContainerRef?.getBoundingClientRect().top -
        emojiPickerRef?.getBoundingClientRect().top)
  );
</script>

<div class="group">
  {#if showExpansion}
    <EmojiExpansion
      {placement}
      bind:expanded
      bind:pickerWidth
      bind:pickerHeight
      bind:virtualListContainerRef
      bind:emojiPickerRef
    />
  {/if}

  <div
    bind:this={emojiPickerRef}
    role="listbox"
    tabindex="0"
    class="ov-focus overflow-hidden rounded-lg border-[0.5px] border-black/20 glossy-effect py-3 shadow-md transition-[height,width] after:rounded-lg dark:border-white/20"
    style={`width: ${pickerWidth}px; height: ${pickerHeight}px; transition-duration: ${EXPANSION_TIME_MS}ms;`}
  >
    <!-- search and skin tone picker section -->
    <div class="flex w-full items-center justify-center gap-3 overflow-hidden px-3">
      <div
        class:dark:shadow-md={viewableStartIndex > 0}
        class:shadow={viewableStartIndex > 0}
        class="flex h-[37px] w-full items-center rounded border-[0.5px] border-black/20 bg-transparent text-sm text-neutral-500 placeholder-neutral-400/80 transition-all hover:border-black/40 focus:outline-none dark:border-white/20 dark:text-neutral-300 dark:placeholder-neutral-400/70 dark:hover:border-white/40"
      >
        {#if !showSkinTones || expanded}
          <input
            type="text"
            class="placeholder-font-normal w-full truncate border-0 bg-inherit text-sm font-medium text-neutral-500 placeholder-neutral-500/70 transition-colors duration-[50ms] focus:ring-0 focus:outline-none dark:text-neutral-300 dark:placeholder-neutral-400/70"
            placeholder="Search emojis..."
            bind:value={filter}
            bind:this={inputRef}
          />
          {#if filter}
            <button
              type="button"
              style={`width: ${EMOJI_SIZE}px; height: ${EMOJI_SIZE}px;`}
              class="ov-focus mr-1 flex shrink-0 items-center justify-center rounded text-neutral-500 transition-colors after:rounded hover:bg-black/15 hover:text-neutral-700 dark:text-neutral-400 dark:hover:bg-white/15 hover:dark:text-neutral-300"
              on:click|stopPropagation={() => (filter = undefined)}
              in:fade={{ duration: 300 }}
            >
              <OVIcon icon={Cross} />
            </button>
          {/if}
        {:else}
          <button
            type="button"
            aria-label="show emoji search"
            on:click|stopPropagation={async () => {
              showSkinTones = !showSkinTones;
              // wait for the the dom to complete the transition before focusing the input
              await tick();
              inputRef?.focus();
            }}
            class="ov-focus flex h-full w-full items-center justify-center after:rounded"
            in:fade={{ duration: 300 }}
          >
            <OVIcon icon={Search} class="text-neutral-500 dark:text-neutral-400" />
          </button>
        {/if}
      </div>

      <div
        class="flex h-[37px] min-w-fit grow flex-nowrap items-center justify-center overflow-hidden rounded border-[0.5px] border-black/20 transition-colors hover:border-black/40 dark:border-white/20 dark:hover:border-white/40"
        class:shadow-lg={viewableStartIndex > 0}
      >
        {#if emojisLabels}
          <SkinTonePicker {emojisLabels} bind:skinIndex={$skinIndex} bind:showSkinTones />
        {/if}
      </div>
    </div>

    <!-- [&>]:scrollbar-thin is an hack to make the scrollbar thinner on the virtual list, we are addressing the first direct child here -->
    <div
      class={cx(
        "flex w-full transition-all [&>]:scrollbar-thin [&>]:scrollbar-invisible [&>]:hover:scrollbar-visible",
        {
          "[mask-image:_linear-gradient(to_top,_transparent_0.5%,_black_6%),_linear-gradient(to_bottom,_transparent_0.5%,_black_4%)] [mask-composite:intersect]": true,
        }
      )}
      bind:this={virtualListContainerRef}
    >
      {#if allEmojisRows.length}
        <VirtualList
          height="{scrollableHeight}px"
          items={allEmojisRowsIndexed}
          bind:start={viewableStartIndex}
          bind:end={viewableEndIndex}
          let:item
        >
          {#if item[1] === 0 && $recentEmojis.length > 0 && !filter}
            <h2
              class="mt-3 mb-2 ml-3 block text-xs font-medium text-neutral-500/70 dark:text-neutral-400"
            >
              Frequently used
            </h2>
          {:else if (item[1] === 1 && $recentEmojis.length > 0 && !filter) || item[1] === 0}
            <h2
              class="mt-3 mb-2 ml-3 block text-xs font-medium text-neutral-500/70 dark:text-neutral-400"
            >
              {filter ? "Search results" : "All emojis"}
            </h2>
          {/if}

          <div class="flex pr-[0.5px] pl-1.5">
            {#each item[0] as emojiObj, index (`${emojiObj.id}-${index}`)}
              {@const emoji = emojiObj.skins[$skinIndex] ?? emojiObj.skins[0]}
              <button
                type="button"
                role="option"
                aria-selected="false"
                title={emojiObj.name}
                style={`width: ${EMOJI_SIZE}px; height: ${EMOJI_SIZE}px;`}
                class="group-one ov-focus flex shrink-0 items-center justify-center rounded p-1 duration-75 after:rounded hover:bg-black/15 active:scale-[0.95] dark:hover:bg-white/15"
                on:click={() => {
                  // this is the emoji selection event dispatched to the parent component
                  dispatch("selection", emoji);
                  // this updates the recent emojis list
                  $recentEmojis = [...new Set([emojiObj, ...$recentEmojis])].slice(0, rowLength);
                }}
              >
                <span class="duration-75 group-one-hover:scale-110 group-one-focus:scale-110">
                  {emoji.native}
                </span>
              </button>
            {/each}
          </div>
        </VirtualList>
      {:else}
        <p class="mt-3 ml-3 text-xs font-medium text-neutral-500/70 dark:text-neutral-400">
          No results found
        </p>
      {/if}
    </div>
  </div>
</div>
